import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  FormulaView,
  WORKING_BOX,
} from "@/components/tools/chemistry/formula-view";
import type { EMPIRICAL_FORMULA_CALCULATOR_STRINGS } from "@/components/tools/empirical-formula-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type EmpiricalResult,
  formulaText,
  type MolecularResult,
} from "@/lib/tool-kit/chemistry/empirical";
import {
  formatFixed,
  formatPlain,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { parseFormula } from "@/lib/tool-kit/chemistry/formula";

type Strings = typeof EMPIRICAL_FORMULA_CALCULATOR_STRINGS;

const MOLES_SIG = 4;
const RATIO_PLACES = 3;
const CLOSE_CALL = 0.05;

function Formula({ text }: { text: string }) {
  const parsed = parseFormula(text);
  return parsed.ok ? (
    <FormulaView formula={parsed.formula} />
  ) : (
    <span>{text}</span>
  );
}

export function EmpiricalResults({
  result,
  molecular,
  molarMass,
  valueHeading,
  strings,
  fmt,
}: {
  result: EmpiricalResult;
  molecular: MolecularResult | null;
  molarMass: number | null;
  valueHeading: string;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const scaled = result.multiplier > 1;
  const molecularText =
    molecular === null
      ? ""
      : formulaText(
          result.steps.map((step) => ({
            symbol: step.symbol,
            subscript: step.subscript * molecular.n,
          }))
        );
  const head = [
    strings.columnElement,
    valueHeading,
    strings.columnWeight,
    strings.columnMoles,
    strings.columnRatio,
    ...(scaled ? [t(strings.columnScaled, { k: fmt(result.multiplier) })] : []),
    strings.columnWhole,
  ];
  const rows = result.steps.map((step) => [
    step.symbol,
    formatPlain(fmt, step.mass),
    formatPlain(fmt, step.weight),
    formatSig(fmt, step.amount, MOLES_SIG),
    formatFixed(fmt, step.ratio, RATIO_PLACES),
    ...(scaled ? [formatFixed(fmt, step.scaled, RATIO_PLACES)] : []),
    formatPlain(fmt, step.subscript),
  ]);
  const smallest = result.steps.reduce((a, b) => (b.amount < a.amount ? b : a));
  const answer = molecular === null ? result.formula : molecularText;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.empirical}
          value={<Formula text={result.formula} />}
        />
        <StatCard
          label={strings.empiricalMass}
          value={`${formatSig(fmt, result.formulaMass, 5)} g/mol`}
        />
        {molecular === null ? null : (
          <StatCard
            emphasis
            label={strings.molecular}
            value={<Formula text={molecularText} />}
          />
        )}
      </StatGrid>
      {result.worstDeviation > CLOSE_CALL ? (
        <Notice>
          {t(strings.closeCall, {
            deviation: formatFixed(fmt, result.worstDeviation, 2),
          })}
        </Notice>
      ) : null}
      {molecular !== null && !molecular.consistent ? (
        <Notice>
          {t(strings.notWhole, {
            multiple: formatFixed(fmt, molecular.multiple, 2),
          })}
        </Notice>
      ) : null}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
        <DataTable head={head} rows={rows} />
      </div>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        {result.steps.map((step) => (
          <p key={step.symbol}>
            {t(strings.moleLine, {
              symbol: step.symbol,
              mass: formatPlain(fmt, step.mass),
              weight: formatPlain(fmt, step.weight),
              moles: formatSig(fmt, step.amount, MOLES_SIG),
            })}
          </p>
        ))}
        <p>
          {t(strings.ratioLine, {
            smallest: smallest.symbol,
            ratios: result.steps
              .map((step) => formatFixed(fmt, step.ratio, RATIO_PLACES))
              .join(" : "),
          })}
        </p>
        {scaled ? (
          <p>
            {t(strings.multiplyLine, {
              k: fmt(result.multiplier),
              whole: result.steps.map((step) => step.subscript).join(" : "),
            })}
          </p>
        ) : null}
        {molecular !== null && molarMass !== null ? (
          <p>
            {t(strings.molecularLine, {
              molar: formatPlain(fmt, molarMass),
              empirical: formatSig(fmt, result.formulaMass, 5),
              multiple: formatFixed(fmt, molecular.multiple, 3),
              n: fmt(molecular.n),
            })}
          </p>
        ) : null}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          empirical: result.formula,
          molecular: answer,
        })}
      />
    </>
  );
}
